'use client';

import { useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

const specs = [
  ['Ingredients', '100% Pure Sardine or Tuna Crude Fish Oil'],
  ['Free Fatty Acid (FFA)', '3% Max'],
  ['Moisture & Impurities', '1% Max'],
  ['Iodine Value', '140 - 165 Min'],
  ['EPA + DHA', '25% Min'],
  ['Anisidine Value (AV)', '25 Max'],
  ['Peroxide Value (PV)', '7 meq/kg Max'],
  ['TOTOX Value', '35 Max'],
  ['Antioxidant', 'Ethoxyquin or BHT (220 PPM Min)'],
];

export default function TechnicalSpecifications({ locale = 'en' }: { locale?: Locale }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isIndonesian = locale === 'id';
  const isVietnamese = locale === 'vn';
  const isChinese = locale === 'cn';

  useEffect(() => {
    if (!sectionRef.current) return;
    const items = sectionRef.current.querySelectorAll('.spec-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.from(items).indexOf(entry.target as Element);
            setTimeout(() => entry.target.classList.add('active'), index * 70);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="specifications" ref={sectionRef} className="py-24 bg-surface-alt scroll-mt-20">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="spec-reveal reveal mb-8 flex items-center gap-3">
          <span className="inline-block w-6 h-px bg-blue" />
          <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
            {isVietnamese
              ? 'Thông số kỹ thuật'
              : isIndonesian
                ? 'Spesifikasi teknis'
                : isChinese
                  ? '技术参数'
                  : 'Technical Specifications'}
          </span>
        </div>

        <div className="spec-reveal reveal reveal-delay-1 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <h2
              className="font-bold tracking-[-0.04em] text-charcoal leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              {isVietnamese
                ? 'Dầu cá cấp thức ăn'
                : isIndonesian
                  ? 'Minyak Ikan Bahan Baku Feed-Grade'
                  : isChinese
                    ? '饲料级鱼油原料'
                    : 'Feed grade fish oil'}
              <br />
              <span className="text-titanium font-light">
                {isVietnamese
                  ? 'được sản xuất đúng thông số, từng lô một.'
                  : isIndonesian
                    ? 'Diproduksi dengan spesifikasi konsisten di setiap batch.'
                    : isChinese
                      ? '按规格生产，批次保持一致。'
                      : 'built to spec, batch after batch.'}
              </span>
            </h2>
          </div>
          <p className="text-titanium text-[15px] leading-relaxed max-w-xl">
            {isVietnamese
              ? 'PT. WWI cung cấp dầu cá thô chất lượng cao, được chiết xuất từ nguồn biển tuyển chọn và ổn định hóa cho ngành dinh dưỡng vật nuôi.'
              : isIndonesian
                ? 'PT. WWI menyediakan Crude Fish Oil berkualitas tinggi, diekstraksi dari sumber laut terpilih dan diformulasikan untuk kebutuhan nutrisi ternak serta akuakultur.'
                : isChinese
                  ? 'PT. WWI 提供高品质粗鱼油，源自精选海洋原料，并针对畜禽与水产营养需求进行稳定化处理。'
                  : 'PT. WWI provides high-quality crude fish oil extracted from premium marine sources and stabilized for the animal nutrition industry.'}
          </p>
        </div>

        <div className="spec-reveal reveal reveal-delay-2 grid grid-cols-1 lg:grid-cols-[1.4fr_0.9fr] gap-6">
          <div className="bg-surface border border-divider rounded-2xl overflow-hidden shadow-card">
            <div className="px-6 py-4 border-b border-divider bg-canvas/60">
              <div className="grid grid-cols-2 text-[11px] font-mono uppercase tracking-[0.14em] text-titanium">
                <span>
                  {isVietnamese ? 'Tham số' : isIndonesian ? 'Parameter' : isChinese ? '参数' : 'Parameter'}
                </span>
                <span>
                  {isVietnamese ? 'Thông số' : isIndonesian ? 'Spesifikasi' : isChinese ? '规格' : 'Specification'}
                </span>
              </div>
            </div>
            <div className="divide-y divide-divider">
              {specs.map(([parameter, specification]) => (
                <div key={parameter} className="grid grid-cols-2 gap-4 px-6 py-4">
                  <div className="text-[13px] font-medium text-charcoal">{parameter}</div>
                  <div className="text-[13px] text-titanium leading-snug">{specification}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-blue text-white rounded-2xl p-6 shadow-card">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase opacity-90 mb-3">
                {isVietnamese
                  ? 'Định vị cốt lõi'
                  : isIndonesian
                    ? 'Posisi inti'
                    : isChinese
                      ? '核心定位'
                      : 'Core positioning'}
              </div>
              <p className="text-[15px] leading-relaxed">
                {isVietnamese
                  ? 'Mật độ năng lượng cao với axit béo omega-3 chuỗi dài cho khâu phần nuôi trồng thủy sản và chăn nuôi.'
                  : isIndonesian
                    ? 'Densitas energi tinggi dengan kandungan asam lemak Omega-3 rantai panjang untuk formulasi akuakultur dan peternakan.'
                    : isChinese
                      ? '高能量密度，并含有长链 Omega-3 脂肪酸，适用于水产与畜牧配方。'
                      : 'High energy density with long-chain omega-3 fatty acids for aquaculture and livestock diets.'}
              </p>
            </div>
            <div className="bg-surface border border-divider rounded-2xl p-6">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-blue mb-3">
                {isVietnamese
                  ? 'Trọng tâm QA'
                  : isIndonesian
                    ? 'Fokus QA'
                    : isChinese
                      ? 'QA 重点'
                      : 'QA Focus'}
              </div>
              <p className="text-[14px] leading-relaxed text-titanium">
                {isVietnamese
                  ? 'Độ ẩm, tạp chất, oxy hóa và độ ổn định axit béo đều được kiểm tra trước khi xuất kho.'
                  : isIndonesian
                    ? 'Kadar air, pengotor, oksidasi, dan konsistensi asam lemak diperiksa sebelum rilis.'
                    : isChinese
                      ? '出货前会严格检验水分、杂质、氧化指标与脂肪酸谱。'
                      : 'Kadar air, kadar kotoran (impurities), parameter oksidasi, dan profil asam lemak diperiksa secara ketat sebelum pengiriman.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

